import { useCallback, useEffect, useId, useRef, type ReactNode } from "react";

/**
 * UC-004 BottomSheet — mobile bottom-anchored sheet (Phase 19, REQ-UI-007).
 *
 * Pattern: tenant dashboard drill-down kalkulace zobrazí Modal na desktop +
 * Bottom-sheet pod `md` breakpoint. Tato komponenta poskytuje mobile variantu;
 * parent komponenta volí `<Modal>` vs `<BottomSheet>` per breakpoint.
 *
 * Accessibility:
 *   - role="dialog" + aria-modal="true"
 *   - aria-labelledby na title element
 *   - Esc → onClose (KeyboardEvent listener na window)
 *   - Focus first focusable po mount; restore focus po close
 *   - Backdrop click closes
 *
 * Touch target ≥ 48×48 px na close button (sdílený mobile gate).
 */

export interface BottomSheetProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
}

export function BottomSheet({ open, title, onClose, children }: BottomSheetProps) {
  const titleId = useId();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    const focusable = containerRef.current?.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    focusable?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      previousFocusRef.current?.focus?.();
    };
  }, [open, onClose]);

  const handleBackdrop = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-end bg-overlay/50"
      onClick={handleBackdrop}
      data-testid="bottom-sheet-backdrop"
    >
      <div
        ref={containerRef}
        className="max-h-[85vh] w-full overflow-y-auto rounded-t-2xl bg-primary px-4 pt-6 pb-8 shadow-lg"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="bottom-sheet"
      >
        <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-secondary" aria-hidden="true" />
        <header className="mb-4 flex items-center justify-between">
          <h2 id={titleId} className="m-0 text-lg font-bold text-primary">
            {title}
          </h2>
          <button
            type="button"
            className="flex min-h-touch min-w-touch cursor-pointer items-center justify-center border-none bg-transparent text-2xl text-primary outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2"
            aria-label="Zavřít panel"
            onClick={onClose}
            data-testid="bottom-sheet-close"
          >
            ×
          </button>
        </header>
        <div>{children}</div>
      </div>
    </div>
  );
}
